import React, { useEffect, useRef, useState } from "react";
import { MotionConfig, motion } from "framer-motion";
import { FaUser, FaEnvelope, FaBook } from "react-icons/fa";
import { IoIosPaper } from "react-icons/io";
import { MdWork, MdOutlineClose } from "react-icons/md";
import { BsFileEarmarkText } from "react-icons/bs";
import Left from "./components/home/Left";
import About from "./components/about/About";
import Resume from "./components/resume/Resume";
import Education from "./components/resume/Education";
import Skills from "./components/resume/Skills";
import Title from "./components/home/Title";
import Projects from "./components/projects/Projects";
import Publications from "./components/blog/Blog";
import Contact from "./components/contact/Contact";
import Sidenav from "./components/home/sidenav/Sidenav";
import ThemeSwitcher from "./components/home/ThemeSwitcher";

// Desktop: unchanged — one "Resume" nav icon, one active-section panel
// rendering <Resume/> (Title/Education/Title/Skills together).
const sections = [
  { id: "about", label: "About", icon: <FaUser />, component: <About /> },
  { id: "resume", label: "Resume", icon: <IoIosPaper />, component: <Resume /> },
  { id: "projects", label: "Projects", icon: <MdWork />, component: <Projects /> },
  {
    id: "publications",
    label: "Publications",
    icon: <FaBook />,
    component: <Publications />,
  },
  { id: "contact", label: "Contact", icon: <FaEnvelope />, component: <Contact /> },
];

// Mobile stacked list only: "Resume" splits into "My Resume" and "My
// Skills" as two independent cards — the same treatment every other
// section already gets (own bg-bodyColor card, own scroll-reveal) — instead
// of both living inside one "Resume" card. Desktop's `sections` above is
// untouched, so the sidenav and single active-section panel don't change.
const mobileSections = [
  { id: "about", component: <About /> },
  {
    id: "resume-experience",
    component: (
      <div className="w-full min-w-0">
        <Title title="My" subTitle="Resume" mobileIndent />
        <Education />
      </div>
    ),
  },
  {
    id: "resume-skills",
    component: (
      <div className="w-full min-w-0">
        <Title title="My" subTitle="Skills" mobileIndent />
        <Skills />
      </div>
    ),
  },
  { id: "projects", component: <Projects /> },
  { id: "publications", component: <Publications /> },
  { id: "contact", component: <Contact /> },
];

const openCV = () => {
  window.open("./Paulo-Drefahl-Software-Engineer-Resume.pdf", "_blank");
};

const Home = () => {
  const [activeSection, setActiveSection] = useState("about");
  const [sidenav, setSidenav] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setSidenav(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const active = sections.find((section) => section.id === activeSection);

  return (
    // reducedMotion="user" makes every framer-motion element below (this
    // section-reveal included) respect prefers-reduced-motion automatically.
    <MotionConfig reducedMotion="user">
    <div className="w-full lgl:w-[85%] h-full lgl:h-[85%] bg-transparent text-textColor z-50 flex items-start justify-between p-4 lgl:p-0">
      {/* ================= Left nav icons ======================== */}
      <div className="w-16 bg-transparent hidden lgl:flex flex-col gap-5">
        <div className="w-full bg-bodyColor rounded-3xl flex flex-col items-center gap-7 py-8">
          {sections.map(({ id, label, icon }) => (
            <span
              key={id}
              onClick={() => setActiveSection(id)}
              className={`${
                activeSection === id ? "text-designColor" : "text-textColor"
              } w-full h-6 text-xl flex items-center justify-center hover:text-designColor duration-300 cursor-pointer relative group`}
            >
              {icon}
              <span className="text-chipColor font-medium text-xs uppercase bg-designColor px-4 py-[1px] rounded-xl absolute left-0 translate-x-8 group-hover:translate-x-12 opacity-0 group-hover:opacity-100 transition-all duration-300 z-20">
                {label}
              </span>
            </span>
          ))}
          <span
            onClick={openCV}
            className="w-full h-6 text-textColor text-xl flex items-center justify-center hover:text-designColor duration-300 cursor-pointer relative group"
          >
            <BsFileEarmarkText />
            <span className="text-chipColor font-medium text-xs uppercase bg-designColor px-4 py-[1px] rounded-xl absolute left-0 translate-x-8 group-hover:translate-x-12 opacity-0 group-hover:opacity-100 transition-all duration-300 z-20">
              CV
            </span>
          </span>
        </div>
        <div
          onClick={() => setSidenav(true)}
          className="w-full h-20 bg-bodyColor rounded-3xl flex justify-center items-center cursor-pointer group"
        >
          <div className="flex flex-col gap-1.5 overflow-hidden">
            <span className="w-8 h-[2px] bg-textColor inline-block -translate-x-2 group-hover:translate-x-0 transition-transform duration-300 group-hover:bg-designColor"></span>
            <span className="w-8 h-[2px] bg-textColor inline-block group-hover:bg-designColor duration-300"></span>
            <span className="w-8 h-[2px] bg-textColor inline-block -translate-x-3.5 group-hover:translate-x-0 transition-transform duration-300 group-hover:bg-designColor"></span>
          </div>
        </div>

        <ThemeSwitcher layout="rail" />

        {/* ============= Sidenav ============= */}
        {sidenav && (
          <div className="w-full h-screen fixed top-0 left-0 bg-black bg-opacity-50 z-50">
            <div className="w-96 h-full relative">
              <motion.div
                ref={ref}
                initial={{ x: -500, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="w-full h-full bg-bodyColor overflow-y-scroll scrollbar-thin scrollbar-thumb-scrollThumb"
              >
                <Sidenav />
                <span
                  onClick={() => setSidenav(false)}
                  className="absolute top-0 -right-16 w-12 h-12 bg-bodyColor text-2xl text-textColor hover:text-designColor duration-300 cursor-pointer flex items-center justify-center z-50"
                >
                  <MdOutlineClose />
                </span>
              </motion.div>
            </div>
          </div>
        )}
      </div>

      {/* ================= Main content ====================== */}
      <div className="w-full lgl:w-[94%] h-full flex flex-col gap-6 lgl:gap-0 lgl:flex-row items-center">
        <Left />
        <div className="w-full lgl:w-8/12 h-[100%] lg:bg-bodyColor sm:bg-transparent rounded-2xl flex justify-center items-center lg:ml-10">
          {/* Smaller devices: all sections stacked. */}
          <div className="w-full h-full lgl:hidden bg-transparent rounded-2xl flex flex-col gap-4">
            {mobileSections.map(({ id, component }) => (
              <div
                key={id}
                className="w-full h-auto bg-bodyColor rounded-2xl p-6 flex justify-center items-center shadow-md"
              >
                {component}
              </div>
            ))}
          </div>

          {/* Theme toggle: the icon rail is desktop-only, so mobile gets its
              own placement, fixed to the bottom-right corner so it's visible
              on the initial screen and stays put while the page scrolls. */}
          <div className="lgl:hidden fixed bottom-4 right-4 z-50">
            <ThemeSwitcher layout="pill" compact />
          </div>

          {/* Larger devices: active section only */}
          <div className="w-full h-[96%] hidden lgl:flex justify-center overflow-y-scroll scrollbar-thin scrollbar-thumb-scrollThumb">
            <motion.div
              key={active.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {active.component}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
    </MotionConfig>
  );
};

export default Home;
